import React, { useCallback } from 'react';

import Link from '@atlaskit/link/link';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

// NOTE on analytics:
// `<CrossflowRecommendationBanner>` (the shared component) already fires
// both `messageInstanceClicked` events AND the standard
// @atlaskit/analytics-next `ui` / `clicked` / `button` events for the CTA
// button (actionSubjectId: `${analyticsId}CtaButton`, clickIdentifier:
// 'ctaButton') and the dismiss button (actionSubjectId:
// `${analyticsId}DismissButton`, clickIdentifier: 'dismissButton').
// We MUST NOT re-fire those here or we'll get duplicate impressions in the
// PO message lifecycle pipeline and the analytics-next stream.
//
// The "Read more about operations" link is OUR addition (rendered inside the
// description chunk) — the shared component knows nothing about it. We
// therefore wire `usePostOfficeUserInteractions` (with a custom
// interactionIdentifier) + the standard ui-clicked event for it ourselves below.
import { CrossflowRecommendationBanner } from '@post-office/browser--jira-werewolves/common/index';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { FormattedMessage, useIntl } from 'react-intl';

import operationsIconSvg from '../assets/operations-icon.svg';
import { registeredContent } from '../placements/in-app-v2/jira-for-you-crossflow-solutions/content';

const LEARN_MORE_URL =
	'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-operations/';

const i18n = registeredContent.default;

type JsmOperationsRecommendationBannerProps = {
	dismiss: () => void;
	onPrimaryAction?: () => void;
};

export const JsmOperationsRecommendationBanner = ({
	dismiss,
	onPrimaryAction,
}: JsmOperationsRecommendationBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	// We only fire analytics for the learn-more link here — CTA and dismiss
	// events are already fired by `<CrossflowRecommendationBanner>`.
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { onCtaActioned } = usePostOfficeUserInteractions();

	const handleCtaClick = useCallback(() => {
		onPrimaryAction?.();
	}, [onPrimaryAction]);

	const handleLearnMoreClick = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'learnMoreLink',
			destinationUrl: LEARN_MORE_URL,
		});
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'jsmOperationsCrossflowRecommendationLearnMore',
			attributes: {
				learnMoreUrl: LEARN_MORE_URL,
			},
		});
	}, [fireAnalyticsEvent, onCtaActioned]);

	const description = (
		<FormattedMessage
			{...i18n.description}
			values={{
				link: (chunks: React.ReactNode) => (
					<Link
						href={LEARN_MORE_URL}
						target="_blank"
						rel="noopener noreferrer"
						onClick={handleLearnMoreClick}
					>
						{chunks}
					</Link>
				),
			}}
		/>
	);

	return (
		<CrossflowRecommendationBanner
			icon={<img src={operationsIconSvg} alt="" width="40" height="40" />}
			title={formatMessage(i18n.title)}
			description={description}
			buttonName={formatMessage(i18n.buttonName)}
			dismissLabel={formatMessage(i18n.dismissLabel)}
			onCtaClick={handleCtaClick}
			dismiss={dismiss}
			analyticsId="jsmOperationsCrossflowRecommendation"
		/>
	);
};
